// Import: turns any HTML (written by PageFlow, by Pandao CMS, by hand) into editable blocks.
// Recognized elements are marked with data-pf; runs of text content are grouped into text blocks;
// anything unknown is kept as it is, in an HTML block.
import { parseFragment } from './sanitize.js';
import { escapeHtml } from './dom.js';
import { getBlock, importOrder } from './registry.js';
import { isColumn } from './grid.js';
import { TEXTUAL_TAGS, INLINE_TAGS } from '../blocks/structure.js';

function isGroupable(node) {
    if (node.nodeType === 3) return node.textContent.trim() !== '';
    if (node.nodeType !== 1) return false;
    const tag = node.localName;
    if (/^h[1-6]$/.test(tag)) return tag !== 'h1' && !node.classList.contains('pflow-heading');
    if (TEXTUAL_TAGS.has(tag)) return true;
    if (tag === 'figure') return !Array.from(node.classList).some(name => name.startsWith('pflow-'));
    if (tag === 'a') {
        // A linked image is an image block; a lone link styled as a button stays text.
        const only = node.children.length === 1 ? node.firstElementChild : null;
        return !(only && only.matches('figure.pflow-img, img') && node.textContent.trim() === '');
    }
    return INLINE_TAGS.has(tag);
}

/** Marks an element as a block, with the editor-only wrapper some blocks need. */
export function markBlock(el, def, ctx) {
    let block = el;
    if (def.wrap) {
        block = ctx.doc.createElement('div');
        block.setAttribute('data-pf-wrapper', '');
        el.replaceWith(block);
        block.append(el);
    }
    block.setAttribute('data-pf', def.name);
    if (typeof def.prepare === 'function') def.prepare(def.wrap ? el : block, ctx);
    return block;
}

function identify(node, ctx, parentKind) {
    for (const def of importOrder()) {
        if (!def.match(node, { ...ctx, parentKind })) continue;
        const el = typeof def.normalize === 'function' ? def.normalize(node, ctx) : node;
        const block = markBlock(el, def, ctx);
        if (def.kind === 'flow' || def.kind === 'col') markFlow(block, ctx);
        else if (def.kind === 'row') markRow(block, ctx);
        return block;
    }
    return null;
}

/** Marks the children of a block that holds other blocks (root, container, box, column). */
export function markFlow(parent, ctx) {
    const textDef = getBlock('text');
    let run = [];
    const flush = () => {
        if (!run.length) return;
        const wrapper = ctx.doc.createElement('div');
        wrapper.className = 'pflow-editor';
        run[0].before(wrapper);
        wrapper.append(...run);
        markBlock(wrapper, textDef, ctx);
        run = [];
    };
    for (const node of Array.from(parent.childNodes)) {
        if (node.nodeType === 8 || (node.nodeType === 3 && !node.textContent.trim())) {
            // Whitespace between blocks is layout of the source only; inside a run it is kept.
            if (run.length && node.nodeType === 3) run.push(node);
            else node.remove();
            continue;
        }
        if (isGroupable(node)) {
            run.push(node);
            continue;
        }
        // Trailing whitespace of a run is not part of the text block.
        while (run.length && run[run.length - 1].nodeType === 3 && !run[run.length - 1].textContent.trim()) run.pop().remove();
        flush();
        if (node.nodeType === 1) identify(node, ctx, 'flow');
    }
    while (run.length && run[run.length - 1].nodeType === 3 && !run[run.length - 1].textContent.trim()) run.pop().remove();
    flush();
}

function markRow(row, ctx) {
    const colDef = getBlock('col');
    for (const node of Array.from(row.childNodes)) {
        if (node.nodeType !== 1) {
            node.remove(); // text between columns is never rendered by a flex row
            continue;
        }
        if (isColumn(node)) {
            markBlock(node, colDef, ctx);
            markFlow(node, ctx);
        } else {
            markBlock(node, getBlock('html'), ctx);
        }
    }
}

/** Parses `html` and returns a fragment of marked blocks, ready for the canvas root. */
export function importHtml(html, ctx) {
    const holder = ctx.doc.createElement('div');
    holder.append(parseFragment(html, ctx.doc, ctx.options.sanitize));
    markFlow(holder, ctx);
    const fragment = ctx.doc.createDocumentFragment();
    fragment.append(...holder.childNodes);
    return fragment;
}

/** Builds a new block from its definition or from preset HTML. */
export function createBlock(name, ctx, presetHtml = null) {
    const def = getBlock(name);
    if (!def) return null;
    if (presetHtml) {
        const fragment = importHtml(presetHtml.replace(/\{([\w.]+)\}/g, (m, key) => escapeHtml(ctx.t(key))), ctx);
        return fragment.firstElementChild ? fragment : null;
    }
    const el = def.create(ctx);
    // A new row or flow block is marked with its content exactly as an imported one.
    const holder = ctx.doc.createElement('div');
    holder.append(el);
    if (def.kind === 'col') {
        markBlock(el, def, ctx);
        markFlow(el, ctx);
    } else {
        const block = identify(el, ctx, 'flow');
        if (!block || block.getAttribute('data-pf') !== name) {
            // The markup of create() is recognized as another block: trust the definition.
            const fresh = def.create(ctx);
            holder.replaceChildren(fresh);
            const marked = markBlock(fresh, def, ctx);
            if (def.kind === 'flow') markFlow(marked, ctx);
            else if (def.kind === 'row') markRow(marked, ctx);
        }
    }
    const fragment = ctx.doc.createDocumentFragment();
    fragment.append(...holder.childNodes);
    return fragment;
}
